import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import {
  CostEstimateOption,
  EducationGoalData,
  EducationType,
  FormValues,
  TuitionType,
  UpsertDTO,
} from './Education.meta';
import { useEducation } from './useEducation';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { SearchSchoolsSelect } from './components/SearchSchoolsSelect/SearchSchoolsSelect';
import { CostBreakdown } from './components/CostBreakdown/CostBreakdown';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { ALREADY_ENROLLED } from '../../Goals.meta';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';

const FORM_ID = 'ProfileGoals--Education';

interface Props extends GoalComponentProps {
  goalData: EducationGoalData;
}

export const Education = ({ clientId, goalData, assetBucketingIndices, ...rest }: Props) => {
  const {
    isSpecificCollege,
    educationType,
    educationTypeOptions,
    costEstimateOptions,
    amount,
    years,
    age,
    ageOptions,
    isStarted,
    name,
    specificCollegeInformation,
    specificCollegeIsPublic,
    specificCollegeIsInState,
    tuitionTypeOptions,
  } = useEducation({ goalData });

  const { control, watch, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      educationType,
      isSpecificCollege,
      amount,
      years,
      age: isStarted ? ALREADY_ENROLLED : age,
      isStarted,
      specificCollegeInformation,
      specificCollegeIsPublic,
      specificCollegeIsInState,
      assetBucketingIndices,
    },
  });

  const [
    watchedIsSpecificCollege,
    watchedEducationType,
    watchedIsStarted,
    watchedSpecificCollegeIsPublic,
    watchedSpecificCollegeIsInState,
    watchedSpecificCollegeInformation,
    watchedAmount,
  ] = watch([
    'isSpecificCollege',
    'educationType',
    'isStarted',
    'specificCollegeIsPublic',
    'specificCollegeIsInState',
    'specificCollegeInformation',
    'amount',
  ]);

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveEducationGoal' });

  const onSubmit = async ({
    specificCollegeInformation: _specificCollegeInformation,
    ...values
  }: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = {
        ...values,
        id: rest.id,
        name,
        age: values.isStarted ? null : values.age,
        specificCollegeName: _specificCollegeInformation?.name || null,
      };

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert variant="error" message={upsertGoalError.message} mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Education goal form"
      >
        <Box role="group" aria-labelledby="education-goal-form--cost-estimate-label">
          <Label id="education-goal-form--cost-estimate-label" css="display: block" mb="4">
            Cost Estimate
          </Label>

          <Controller
            name="isSpecificCollege"
            control={control}
            render={({ field: { value, name: _name, onChange } }) => (
              <Box display="flex" flexDirection="row" css="gap: 8px">
                {costEstimateOptions.map(option => (
                  <Radio
                    key={option.value}
                    name={_name}
                    checked={option.value === CostEstimateOption.SpecificCollege ? value : !value}
                    onChange={() => onChange(option.value === CostEstimateOption.SpecificCollege)}
                  >
                    {option.label}
                  </Radio>
                ))}
              </Box>
            )}
          />
        </Box>

        {watchedIsSpecificCollege ? (
          <>
            <SearchSchoolsSelect
              clientId={clientId}
              label="School"
              defaultValue={
                goalData.specificCollegeInformation
                  ? {
                      value: goalData.specificCollegeInformation.id,
                      label: goalData.specificCollegeInformation.name,
                      data: goalData.specificCollegeInformation,
                    }
                  : null
              }
              onChange={opt => {
                if (opt) {
                  setValue('specificCollegeInformation', opt.data);
                  setValue(
                    'amount',
                    watchedSpecificCollegeIsInState
                      ? opt.data.inStateTotal
                      : opt.data.outOfStateTotal,
                  );
                  setValue('specificCollegeIsPublic', !opt.data.isPrivate);
                } else {
                  setValue('specificCollegeInformation', null);
                  setValue('amount', 0);
                  setValue('specificCollegeIsPublic', false);
                }
              }}
              placeholder="Search for a school..."
              openMenuOnClick={false}
              isClearable
            />

            {watchedSpecificCollegeIsPublic && watchedSpecificCollegeInformation && (
              <Controller
                control={control}
                name="specificCollegeIsInState"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="Tuition Type"
                    value={tuitionTypeOptions.find(
                      ({ value: v }) =>
                        v === (value ? TuitionType.InState : TuitionType.OutOfState),
                    )}
                    options={tuitionTypeOptions}
                    onChange={opt => {
                      onChange(opt?.value === TuitionType.InState);

                      setValue(
                        'amount',
                        opt?.value === TuitionType.InState
                          ? watchedSpecificCollegeInformation.inStateTotal
                          : watchedSpecificCollegeInformation.outOfStateTotal,
                      );
                    }}
                    isPortal
                  />
                )}
              />
            )}

            <Controller
              control={control}
              name="amount"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Cost"
                  tooltip={
                    watchedSpecificCollegeInformation && (
                      <CostBreakdown
                        school={watchedSpecificCollegeInformation}
                        isInState={watchedSpecificCollegeIsInState}
                        amount={watchedAmount}
                      />
                    )
                  }
                  leftIcon="far fa-dollar-sign"
                  innerText="/year"
                  decimalScale={2}
                  allowNegative={false}
                  maxLength={14}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />
          </>
        ) : (
          <>
            <Box
              display="grid"
              css={`
                grid-template-columns: ${watchedEducationType === EducationType.Other
                  ? '1fr 1fr'
                  : '1fr'};
                gap: 16px;
              `}
            >
              <Controller
                control={control}
                name="educationType"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="Education Type"
                    tooltip="These figures are based on current averages from the College Board website and include tuition, room and board."
                    value={educationTypeOptions.find(({ value: v }) => value === v)}
                    options={educationTypeOptions}
                    onChange={opt => onChange(opt?.value)}
                    isPortal
                  />
                )}
              />

              {watchedEducationType === EducationType.Other && (
                <Controller
                  control={control}
                  name="amount"
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      label="Amount"
                      leftIcon="far fa-dollar-sign"
                      innerText="/year"
                      decimalScale={2}
                      allowNegative={false}
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                      {...field}
                    />
                  )}
                />
              )}
            </Box>
          </>
        )}

        <Box
          display="grid"
          css={`
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          `}
        >
          <Controller
            control={control}
            name="age"
            render={({ field: { value, onChange } }) => (
              <Select
                label="Enrollment Age"
                value={value ? { value, label: value } : null}
                options={ageOptions}
                onChange={opt => {
                  onChange(opt?.value);
                  setValue('isStarted', opt?.value === ALREADY_ENROLLED);
                }}
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name="years"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label={`Study Years ${watchedIsStarted ? 'Remaining' : ''}`}
                decimalScale={0}
                thousandSeparator={false}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                {...field}
              />
            )}
          />
        </Box>

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
